import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { CreditCard, Loader2, Check, Coins, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// PayPal icon SVG
const PayPalIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.407c2.145 0 3.716.467 4.66 1.389.94.916 1.322 2.283 1.135 4.065-.012.112-.026.223-.043.333a7.157 7.157 0 0 1-.231 1.096c-.746 2.725-3.13 4.118-6.683 4.118h-1.49a.768.768 0 0 0-.758.644l-.755 4.616zm2.166-5.68h1.168c2.612 0 4.378-.893 4.96-2.506.256-.707.343-1.371.23-1.976-.118-.614-.432-1.097-.932-1.438-.514-.351-1.239-.529-2.156-.529H9.81l-1.12 6.449h.552z"/>
  </svg>
);

export default function BuyCreditsModal({ open, onOpenChange, onSuccess }) {
  const { token, user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [currency, setCurrency] = useState('GBP');
  const [pricePerCredit, setPricePerCredit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customCredits, setCustomCredits] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [availableMethods, setAvailableMethods] = useState([]);

  useEffect(() => {
    if (open) {
      fetchPackages();
      fetchPaymentMethods();
    }
  }, [open]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/payments/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data.packages);
      setCurrency(response.data.currency);
      setPricePerCredit(response.data.price_per_credit);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/api/payments/methods`);
      setAvailableMethods(response.data.methods);
      if (response.data.methods.length > 0) {
        setPaymentMethod(response.data.methods[0].id);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const handlePurchase = async () => {
    let credits = selectedPackage ? selectedPackage.credits : parseInt(customCredits);
    
    if (!credits || credits < 1) {
      toast.error('Please select a package or enter a valid credit amount');
      return;
    }

    setProcessing(true);

    try {
      if (paymentMethod === 'stripe') {
        // Create Stripe checkout
        const response = await axios.post(
          `${API}/api/payments/stripe/create-checkout?credits=${credits}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else if (paymentMethod === 'paypal') {
        // Create PayPal order
        const response = await axios.post(
          `${API}/api/payments/paypal/create-order?credits=${credits}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Redirect to PayPal approval
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setCustomCredits('');
  };

  const handleCustomCreditsChange = (value) => {
    setCustomCredits(value);
    setSelectedPackage(null);
  };

  const getCustomPrice = () => {
    const credits = parseInt(customCredits);
    if (!credits || credits < 1) return 0;
    // Apply discount based on quantity
    let discount = 0;
    if (credits >= 100) discount = 0.25;
    else if (credits >= 50) discount = 0.20;
    else if (credits >= 25) discount = 0.15;
    else if (credits >= 10) discount = 0.10;
    else if (credits >= 5) discount = 0.05;
    return pricePerCredit * credits * (1 - discount);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Coins className="w-6 h-6 text-lime-500" />
            Buy Interview Credits
          </DialogTitle>
          <DialogDescription>
            Purchase credits to create interview applications. Credits never expire.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Credit Packages */}
            <div>
              <Label className="text-base font-medium mb-3 block">Select a Package</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {packages.map((pkg) => (
                  <Card
                    key={pkg.id}
                    className={`cursor-pointer transition-all ${
                      selectedPackage?.id === pkg.id
                        ? 'border-lime-500 ring-2 ring-lime-500 bg-lime-500/5'
                        : 'hover:border-lime-500/50'
                    } ${pkg.popular ? 'relative' : ''}`}
                    onClick={() => handlePackageSelect(pkg)}
                    data-testid={`package-${pkg.id}`}
                  >
                    {pkg.popular && (
                      <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-lime-500">
                        Popular
                      </Badge>
                    )}
                    <CardContent className="pt-6 text-center">
                      <p className="text-2xl font-bold">{pkg.credits}</p>
                      <p className="text-sm text-muted-foreground">Credits</p>
                      <p className="text-lg font-semibold mt-2">{formatPrice(pkg.price)}</p>
                      {pkg.discount && (
                        <Badge variant="secondary" className="mt-2 text-xs">
                          {pkg.discount}
                        </Badge>
                      )}
                      {selectedPackage?.id === pkg.id && (
                        <Check className="w-5 h-5 text-lime-500 mx-auto mt-2" />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium mb-3 block">Or Enter Custom Amount</Label>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Enter number of credits"
                    value={customCredits}
                    onChange={(e) => handleCustomCreditsChange(e.target.value)}
                    className={customCredits ? 'border-lime-500' : ''}
                    data-testid="custom-credits-input"
                  />
                </div>
                {customCredits && parseInt(customCredits) > 0 && (
                  <div className="text-right">
                    <p className="text-lg font-semibold">{formatPrice(getCustomPrice())}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatPrice(getCustomPrice() / parseInt(customCredits))}/credit
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method Selection */}
            {availableMethods.length > 1 && (
              <div className="border-t pt-4">
                <Label className="text-base font-medium mb-3 block">Payment Method</Label>
                <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
                  <TabsList className="grid w-full grid-cols-2">
                    {availableMethods.map((method) => (
                      <TabsTrigger key={method.id} value={method.id} className="flex items-center gap-2">
                        {method.id === 'stripe' && <CreditCard className="w-4 h-4" />}
                        {method.id === 'paypal' && <PayPalIcon />}
                        {method.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            )}

            {/* Summary */}
            {(selectedPackage || (customCredits && parseInt(customCredits) > 0)) && (
              <div className="bg-muted/50 rounded-lg p-4 border">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">Order Summary</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedPackage ? selectedPackage.credits : customCredits} interview credits
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {formatPrice(selectedPackage ? selectedPackage.price : getCustomPrice())}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Purchase Button */}
            <Button
              className="w-full bg-lime-500 hover:bg-lime-600 text-white h-12 text-lg"
              onClick={handlePurchase}
              disabled={processing || (!selectedPackage && (!customCredits || parseInt(customCredits) < 1))}
              data-testid="purchase-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {paymentMethod === 'stripe' && <CreditCard className="w-5 h-5 mr-2" />}
                  {paymentMethod === 'paypal' && <span className="mr-2"><PayPalIcon /></span>}
                  Pay with {paymentMethod === 'stripe' ? 'Card' : 'PayPal'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Secure payment processed by {paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}. 
              Credits will be added immediately after successful payment.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
